import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
} from "react";

import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import _ from "lodash";
import { t } from "i18next";

import CustomInput from "../inputs";
import PortalContext from "../../context/portalContext";
import DataContext from "../../context/dataContext";
import { useCheckMail, useRegisterUser } from "../../apiHooks/auth";

import { FlexCenter } from "../../styles";
import { closeSvg, linkArrowLeft, brandLogoSm, errorSvg } from "../../svgs";

import CustomGoogleAuth from "../customGoogleAuth";
import CustomFacebookAuth from "../customFacebookAuth";

import { ModalWrapper, AuthContent, AuthPrimaryBtn } from "./styled";
import ErrorNotification from "./notifications";

const RegisterModal: React.FC = () => {
  const { closeAuthPortals, openLogin } = useContext(PortalContext);
  const {
    authErrorObject: serverErrors,
    setAuthErrorObject,
    setAuthErrors,
  } = useContext(DataContext);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const modalWrapper = useRef();
  const [isOpen, setIsOpen] = useState(false);
  const [mailChecking, setMailCheck] = useState(false);

  const [passMatch, passwordMatch] = useState(true);

  const { registerUser, loading: registerLoading } = useRegisterUser();

  const onSubmit = (data: {
    password: string;
    password2: string;
    username: string;
    phone: string;
    email: string;
  }) => {
    setAuthErrorObject([]);
    // console.log(data);
    if (data.password2 !== data.password) {
      passwordMatch(false);
    } else {
      passwordMatch(true);
    }
    if (!mailChecking && data.password2 === data.password) {
      const modifyPhone = (phone: string) => {
        const firstPart = phone.substring(0, 3);
        // if home number
        if (firstPart.includes("322")) return `+${phone}`;

        // if is prefix
        if (firstPart.includes("995")) return `+${phone}`;
        return `+995${phone}`;
      };
      registerUser({
        params: {
          ...data,
          email: data.email.trim(),
          phone: modifyPhone(data?.phone),
        },
      });
    }
  };

  // check meil avalibility
  const { checkMail } = useCheckMail();

  const handleEmailCheck = async (event) => {
    setMailCheck(true);
    checkMail({ email: event.target.value });

    setMailCheck(false);
    document.getElementById("email").focus();
  };

  const checkEmail = useCallback(
    _.debounce(handleEmailCheck, 250, { maxWait: 1000 }),
    []
  );

  const outsideClick = (e) => {
    return (
      e.target === modalWrapper.current &&
      !registerLoading &&
      closeAuthPortals()
    );
  };
  useEffect(() => {
    setIsOpen(true);
    window.addEventListener("click", outsideClick);
    document.querySelector("body").classList.add("fixed");
    // dispatch(clearSignIn());
    // dispatch(checkAnalyticsId('register_popup'));

    return () => {
      window.removeEventListener("click", outsideClick);
      document.querySelector("body").classList.remove("fixed");
      setAuthErrors([]);
      setAuthErrorObject([]);
      // setIsOpen(false);
    };
  }, []);

  //  ----- manage errors -----
  const [showSingleError, setSingleError] = useState(false);
  useEffect(() => {
    // password errors
    const passwordErrors =
      errors.password === undefined &&
      errors.password2 === undefined &&
      passMatch
        ? 0
        : 1;

    // all auth errors except password and password2
    const basicLocalErrors = Object.keys(errors).filter(
      (key) => key !== "password" && key !== "password2"
    );

    // length of auth errros (3)
    const localErrorLength = Object.keys(basicLocalErrors).length;

    const avalebiltyError = 0;

    // if single errror is more or less than 1 global wanrrning should appear
    const singleError =
      localErrorLength + serverErrors.length + avalebiltyError + passwordErrors;
    if (singleError === 1) {
      setSingleError(true);
    } else {
      setSingleError(false);
    }
  }, [serverErrors, Object.keys(errors)?.length, passMatch]);

  // reset server error on Client error change
  useEffect(() => {
    setAuthErrorObject([]);
  }, [Object.keys(errors)?.length, passMatch]);

  // re-order validation patterns/types
  const validateFullName = (value) => {
    const pattern = /^[a-zA-Zა-ჰ\s!"#$%&'()*+,-./:;<=>?@[\\\]^_`{|}~]*$/;
    const minLength = 2;
    const maxLength = 30;

    if (!value) {
      return t("fullNameRequired");
    }

    if (!pattern.test(value)) {
      return t("fullNameInvalidCharacters");
    }

    if (value.length < minLength) {
      return t("fullNameCharacters1");
    }
    if (value.length > maxLength) {
      return t("fullNameCharacters2");
    }

    return true;
  };
  const validatePassword = (value) => {
    const pattern = /^[a-zA-Z0-9 !@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+$/;
    const minLength = 8;
    if (!value) {
      return t("passwordRequired");
    }
    if (!pattern.test(value)) {
      return t("passwordInvalid");
    }
    if (value.length < minLength) {
      return t("passwordCharacters");
    }
    return true;
  };
  // console.log(errors?.password, passMatch);

  return (
    <ModalWrapper open={true} ref={modalWrapper}>
      <AuthContent className="registe-mobile">
        <button
          className="close"
          onClick={() => !registerLoading && closeAuthPortals()}
          style={{ opacity: `${registerLoading ? "0.5" : "1"}` }}
        >
          {closeSvg()}
          <FlexCenter>{linkArrowLeft()}</FlexCenter>
        </button>

        <div className="auth-content">
          {/* for mobile */}
          <Link to="/" role="none" className="brand-logo">
            {brandLogoSm()}
          </Link>

          <div className="heading">
            <span>შექმენი ანგარიში</span>
            <span className="mobile">შოპინგი იწყება აქ</span>
          </div>

          <FlexCenter
            className="switch-auth"
            style={{ opacity: `${registerLoading ? "0.5" : "1"}` }}
          >
            <button onClick={openLogin}>ავტორიზაცია </button>
            <button className="active">რეგისტრაცია </button>
          </FlexCenter>

          {/* ---  display error if multiple fields has error --- */}
          {!showSingleError &&
          (!!Object.keys(errors).length ||
            !passMatch ||
            !!Object.keys(serverErrors).length) ? (
            <ErrorNotification globalErrors={[]} />
          ) : null}

          <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
            {/* email */}
            {(serverErrors?.find((el) => el.type === "email") ||
              Object.keys(errors).find((el) => el === "email")) &&
            showSingleError ? (
              <div className="error">
                {errorSvg()}
                <span>
                  {serverErrors?.find((el) => el.type === "email")?.msg ||
                    (Object.keys(errors).find((key) => key === "email") &&
                      errors.email?.message?.toString())}
                </span>
              </div>
            ) : null}
            <CustomInput
              margin="0 0 12px"
              inputType="text"
              id="email"
              inputName="მეილი"
              handleChange={checkEmail}
              reactHookOptions={register("email", {
                required: {
                  value: true,
                  message: t("emailRequired"),
                },
                pattern: {
                  value:
                    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                  message: t("emailNotValid"),
                },
              })}
              errors={
                errors.email || serverErrors?.find((el) => el.type === "email")
              }
              disabled={!!mailChecking}
              // errorMessages={<>{errors.email && <span>fieldIsRequired</span>} </>}
              className={`${mailChecking ? "-loading" : ""}`}
            />

            {/* username */}
            {errors.username && showSingleError ? (
              <div className="error">
                {errorSvg()}
                <span>{errors?.username?.message?.toString()}</span>
              </div>
            ) : null}
            <CustomInput
              margin="0 0 12px"
              inputType="text"
              id="username"
              inputName="სახელი, გვარი"
              reactHookOptions={register("username", {
                validate: validateFullName,
              })}
              errors={
                errors.username ||
                serverErrors?.find((el) => el.type === "username")
              }
              // errorMessages={<>{errors.username && <span>fieldIsRequired</span>} </>}
            />

            {/* phone */}
            {(errors.phone || serverErrors.find((el) => el.type === "phone")) &&
            showSingleError ? (
              <div className="error">
                {errorSvg()}
                <span>
                  {serverErrors.find((el) => el.type === "phone")?.msg ||
                    errors?.phone?.message?.toString()}
                </span>
              </div>
            ) : null}
            <CustomInput
              margin="0 0 12px"
              inputType="number"
              id="phone"
              inputName="მობილურის ნომერი"
              reactHookOptions={register("phone", {
                required: {
                  value: true,
                  message: t("phoneRequired"),
                },
                minLength: {
                  value: 9,
                  message: t("phoneCharacters"),
                },
                maxLength: {
                  value: 13,
                  message: t("phoneCharacters2"),
                },
              })}
              errors={
                errors.phone || serverErrors.find((el) => el.type === "phone")
              }
            />

            {/* password */}
            {(errors.password ||
              errors.password2 ||
              !passMatch ||
              serverErrors.find((el) => el.type === "password")) &&
            showSingleError ? (
              <div className="error">
                {errorSvg()}
                <span>
                  {serverErrors.find((el) => el.type === "password")?.msg ||
                    errors?.password?.message ||
                    errors?.password2?.message ||
                    (!passMatch ? t("passwordNotMatch") : null)}
                </span>
              </div>
            ) : null}
            <div className="passwords">
              <CustomInput
                margin="0 0 12px"
                inputType="password"
                id="signup-password"
                inputName="პაროლი"
                reactHookOptions={register("password", {
                  validate: validatePassword,
                })}
                errors={
                  errors.password ||
                  !passMatch ||
                  serverErrors.find((el) => el.type === "password")
                }
                // errorMessages={<>{errors.password && <span>fieldIsRequired</span>}</>}
              />
              <CustomInput
                margin="0 0 12px"
                inputType="password"
                id="signup-password-repeat"
                inputName="გაიმეორე პაროლი"
                reactHookOptions={register("password2", {
                  required: {
                    value: true,
                    message: t("repeatPasswordRequired"),
                  },
                  minLength: {
                    value: 8,
                    message: t("passwordCharacters"),
                  },
                })}
                errors={errors.password2 || !passMatch}
                // errorMessages={<>{errors.password2 && <span>fieldIsRequired</span>}</>}
              />
            </div>

            <div className="terms">
              რეგისტრაციის ღილაკზე დაჭერით ავტომატურად ეთანხმები ველისწესებს და
              პირობებს
              <a
                href="https://veli.store/info/terms-and-conditions/"
                target="blank"
              >
                ველისწესებს და პირობებს
              </a>
            </div>

            <AuthPrimaryBtn
              className={`${
                mailChecking || registerLoading ? "-disabled" : ""
              }`}
            >
              შექმენი ანგარიში
            </AuthPrimaryBtn>
          </form>

          <div className="divider">
            <span>ან</span>
          </div>

          <div
            className="alt-auth"
            style={{ opacity: `${registerLoading ? "0.5" : "1"}` }}
          >
            {/* <button> {fbColoredSvg()} Sign in with Facebook </button>
            <button> {googleSvg()} Sign in with Google </button> */}

            <CustomFacebookAuth />
            <CustomGoogleAuth />
          </div>
        </div>
      </AuthContent>
    </ModalWrapper>
  );
};

export default RegisterModal;
